import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  type Auth,
  type ConfirmationResult,
} from "firebase/auth";
import { authService } from "@/features/auth/services/auth.service";

/**
 * Lazy(-ish) Firebase client bootstrap.
 *
 * The Web SDK config is fetched from bizpandit-api's `/public/firebase-config`
 * on first use rather than pulled from `.env` at build time — this keeps a
 * single source of truth (backend already needs the corresponding admin
 * credentials) and lets Ops rotate the Firebase project without a frontend
 * rebuild. Everything below is memoized (as a Promise, not just the
 * resolved value) so concurrent callers share one in-flight request instead
 * of racing to create duplicate apps/verifiers.
 *
 * IMPORTANT: initializing the app (config fetch) + rendering the invisible
 * reCAPTCHA widget (Google script injection) together commonly take 1-4s.
 * That cost used to be paid entirely on the user's first "Send OTP" click,
 * stacked in front of the actual SMS trigger. `warmUpFirebase()` lets a
 * screen that's *about* to need this (e.g. the mobile-entry step of
 * register/login) kick it off in the background the moment it mounts, so
 * by the time the user finishes typing their number and hits Send, this is
 * usually already resolved.
 */
const RECAPTCHA_CONTAINER_ID = "firebase-recaptcha-container";

let appPromise: Promise<FirebaseApp> | null = null;
let authPromise: Promise<Auth> | null = null;
let recaptchaPromise: Promise<RecaptchaVerifier> | null = null;
let recaptchaInstance: RecaptchaVerifier | null = null;

async function getApp(): Promise<FirebaseApp> {
  if (!appPromise) {
    appPromise = authService.getFirebaseConfig().then((config) => initializeApp(config));
  }
  return appPromise;
}

export async function getFirebaseAuth(): Promise<Auth> {
  if (!authPromise) {
    authPromise = getApp().then((app) => getAuth(app));
  }
  return authPromise;
}

function removeRecaptchaContainer() {
  document.getElementById(RECAPTCHA_CONTAINER_ID)?.remove();
}

/**
 * Always creates a FRESH container node rather than reusing whatever's
 * already in the DOM. Reusing an old node across `RecaptchaVerifier`
 * instances (e.g. after a previous `.clear()`, or after Vite HMR
 * re-executes this module in dev while the old DOM node survives) is
 * exactly what triggers grecaptcha's "client element has been removed: 0"
 * error — it still has internal state tied to that node id from the
 * previous widget.
 */
function createRecaptchaContainer(): HTMLElement {
  removeRecaptchaContainer();
  const container = document.createElement("div");
  container.id = RECAPTCHA_CONTAINER_ID;
  // NOTE: deliberately NOT `display: none`. grecaptcha needs the node to
  // actually be laid out (non-zero, has an offsetParent) to bind its
  // internal widget correctly — a `display:none` container is exactly
  // what produces "reCAPTCHA client element has been removed: N" the
  // moment `execute()`/`render()` tries to use it, even on a first-ever
  // render with no prior widget in play. Positioning off-screen keeps it
  // out of the visual layout while still being a real, rendered element.
  container.style.position = "fixed";
  container.style.top = "-9999px";
  container.style.left = "-9999px";
  document.body.appendChild(container);
  return container;
}

/**
 * Firebase requires a visible-or-invisible reCAPTCHA widget per browser
 * session for the anti-abuse check. We render an invisible one anchored
 * to a hidden div. Any caller gets the same in-flight/resolved singleton
 * back — until `teardownRecaptcha()` runs, which forces the next call to
 * build a completely fresh widget + container.
 */
function getRecaptchaVerifier(auth: Auth): Promise<RecaptchaVerifier> {
  if (!recaptchaPromise) {
    recaptchaPromise = (async () => {
      const container = createRecaptchaContainer();
      const verifier = new RecaptchaVerifier(auth, container, { size: "invisible" });
      await verifier.render();
      recaptchaInstance = verifier;
      return verifier;
    })().catch((err) => {
      // Don't leave a rejected promise memoized forever — let the next
      // caller retry from scratch instead of being stuck with a
      // permanently-broken cache.
      recaptchaPromise = null;
      recaptchaInstance = null;
      throw err;
    });
  }
  return recaptchaPromise;
}

/** Fully tears down the current reCAPTCHA widget + its DOM node so the next call builds a clean one. */
function teardownRecaptcha() {
  if (recaptchaInstance) {
    try {
      recaptchaInstance.clear();
    } catch {
      /* already detached/cleared — fine */
    }
  }
  removeRecaptchaContainer();
  recaptchaPromise = null;
  recaptchaInstance = null;
}

/**
 * Pre-warms Firebase app init + invisible reCAPTCHA render in the
 * background as soon as a phone-auth screen mounts. Safe to call multiple
 * times / from multiple components (memoized) and safe to ignore failures
 * here — the real error surfaces normally when `sendPhoneOtp()` actually
 * runs, this is purely a head start.
 */
export function warmUpFirebase(): void {
  void getFirebaseAuth()
    .then((auth) => getRecaptchaVerifier(auth))
    .catch(() => {
      /* surfaced properly on the real send attempt */
    });
}

/** Rejects `promise` with a clear, user-facing error if it hasn't settled within `ms`. */
function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

/** True for the known "the reCAPTCHA widget/DOM node is stale" failure modes. */
function isStaleRecaptchaError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err);
  const code = (err as { code?: string } | null)?.code;
  return (
    message.includes("client element has been removed") ||
    message.includes("already been rendered") ||
    code === "auth/captcha-check-failed" ||
    code === "auth/internal-error"
  );
}

/** Normalize a raw 10-digit Indian mobile to E.164, matching backend's regex. */
export function toE164India(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (raw.startsWith("+")) return `+${digits}`;
  if (digits.startsWith("91") && digits.length === 12) return `+${digits}`;
  return `+91${digits}`;
}

/**
 * Kicks off Firebase Phone Auth: sends the SMS, returns a
 * `ConfirmationResult` the caller uses to submit the code the user typed.
 * Also hits our backend `/auth/send-otp` as a rate-limit gate so a bad
 * actor can't blow through Firebase's quota per phone number.
 *
 * The rate-limit call and the Firebase app/auth warm-up are independent of
 * each other, so they run concurrently (`Promise.all`) instead of the
 * previous fully-sequential chain — that alone removes one full network
 * round-trip's worth of pure latency from the critical path. Every step
 * also has a bounded timeout so a stalled reCAPTCHA script or a hung
 * Firebase call surfaces as a real error instead of an indefinite
 * "Sending..." with nothing ever resolving or rejecting. If the reCAPTCHA
 * widget turns out to be stale (see `isStaleRecaptchaError`), we tear it
 * down and retry once with a completely fresh one before giving up.
 */
export async function sendPhoneOtp(phoneE164: string): Promise<ConfirmationResult> {
  const [, auth] = await Promise.all([
    authService.sendOtp({ phone: phoneE164 }),
    getFirebaseAuth(),
  ]);

  const attemptSend = async (): Promise<ConfirmationResult> => {
    const verifier = await getRecaptchaVerifier(auth);
    return withTimeout(
      signInWithPhoneNumber(auth, phoneE164, verifier),
      20_000,
      "Sending the code timed out. Try again.",
    );
  };

  try {
    return await attemptSend();
  } catch (err) {
    if (!isStaleRecaptchaError(err)) throw err;
    teardownRecaptcha();
    return attemptSend();
  }
}

/**
 * Confirms the OTP the user typed against the `ConfirmationResult`
 * returned by `sendPhoneOtp`. Returns the Firebase ID token that must
 * then be posted to `/auth/verify-otp` for the backend to trust the
 * verification.
 */
export async function confirmPhoneOtp(
  confirmation: ConfirmationResult,
  code: string,
): Promise<string> {
  const credential = await confirmation.confirm(code);
  return credential.user.getIdToken();
}

/** Test hook — lets `LoginForm`/`LogoutButton` fully reset Firebase state. */
export function resetFirebaseClient() {
  appPromise = null;
  authPromise = null;
  teardownRecaptcha();
}

export type { ConfirmationResult };
